import { useEffect, useState } from 'react';
import { TestCollection, TestCollectionPaginated } from './TestCollectionInterfaces';
import styles from './TestCollectionList.module.css';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import { Button, Pagination } from '@dsi/react-eds';
import { useLoader } from '../../../hooks/useLoader';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import type { ColumnHelper } from '@tanstack/react-table';
import { useReactTable, getCoreRowModel, createColumnHelper, flexRender, ColumnFiltersState } from '@tanstack/react-table';
import { useTheme } from '../../../hooks/useTheme';
import api from '../../../api/axiosInstance';
import { AxiosError } from 'axios';
import DeleteTestCollectionDialog from './DeleteTestCollectionDialog';
import { useWebSocketService } from '../../../hooks/useWebSocketService';

const columnHelper: ColumnHelper<TestCollection> = createColumnHelper<TestCollection>();
function TestCollectionList() {
  const { theme } = useTheme();
  const [completeData, setCompleteData] = useState<TestCollectionPaginated>();
  const [data, setData] = useState<TestCollection[]>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [filterTimeout, setFilterTimeout] = useState<NodeJS.Timeout | null>(null);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTC, setSelectedTC] = useState<TestCollection>();
  const { showLoader, hideLoader } = useLoader();
  const { addNotifications } = useSwobNotifications();

  const ws = useWebSocketService();

  const fetchTestCollections = async (pageIndex: number, pageSize?: number) => {
    try {
      showLoader();
      const size = pageSize || table.getState().pagination.pageSize;
      let baseApi = `/api/test-collections/paginated?limit=${size}&offset=${pageIndex * size}`;
      if (columnFilters) {
        console.log(columnFilters);
        columnFilters?.forEach((filter) => {
          if (filter.id === 'testCollectionName' && filter.value !== '') baseApi = `${baseApi}&name=${filter.value}`;
          if (filter.id === 'testServiceName' && filter.value !== '') baseApi = `${baseApi}&test_service=${filter.value}`;
          if (filter.id === 'productLine' && filter.value !== '') baseApi = `${baseApi}&product_line=${filter.value}`;
          if (filter.id === 'productName' && filter.value !== '') baseApi = `${baseApi}&product_number=${filter.value}`;
          if (filter.id === 'productVersion' && filter.value !== '') baseApi = `${baseApi}&product_version=${filter.value}`;
        });
      }

      const res = await api.get(baseApi);
      table.setPageSize(res.data.limit);
      table.options.rowCount = res.data.total;
      table.options.pageCount = getPageCount(res.data.total, res.data.limit);
      setCompleteData(res.data);
      setData(res.data.data);
      hideLoader();
    } catch (error) {
      // show error
      console.log(error);
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError
              ? error.response?.data?.message || error.message
              : 'An error occurred fetching Test Collections',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
      hideLoader();
    }
  };

  const getPageCount = (total: number, limit: number) => {
    if (total % limit === 0) {
      return total / limit;
    } else {
      return Math.ceil(total / limit);
    }
  };

  const onDeleteTC = async (testCollection: TestCollection) => {
    setSelectedTC(testCollection);
    setDialogOpen(true);
  };

  useEffect(() => {
    const unsubscribe = ws?.onEntityChange('TEST_COLLECTION', (data) => {
      if (data) {
        const { pageIndex, pageSize } = pagination;
        fetchTestCollections(pageIndex, pageSize);
      } else {
        addNotifications([
          {
            title: 'Websocket Error',
            icon: 'icon-triangle-warning',
            description: 'An error occurred with the websocket. ',
            timestamp: new Date()
          }
        ]);
      }
    });

    fetchTestCollections(0, 10);

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { pageIndex, pageSize } = pagination;
    fetchTestCollections(pageIndex, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters]);

  const columns = () => [
    columnHelper.accessor('testCollectionName', {
      header: 'Test Collection Name',
      cell: (info) => info.getValue()
    }),
    columnHelper.accessor('testServiceName', {
      header: 'Test Service',
      cell: (info) => info.getValue()
    }),
    columnHelper.accessor('productLine', {
      header: 'Product Line',
      cell: (info) => info.getValue()
    }),
    columnHelper.accessor('productNumber', {
      header: 'Product Number',
      cell: (info) => info.getValue()
    }),
    columnHelper.accessor('productVersion', {
      header: 'Product Version',
      cell: (info) => info.getValue()
    }),
    columnHelper.display({
      id: 'actions',
      cell: (info) => (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <ReactEDSWrapper
            component={Button}
            className={`btn icon-btn ${styles.iconBtn}`}
            aria-label="Delete"
            onClick={() => onDeleteTC(info.row.original)}
          >
            <i className="icon icon-trashcan"></i>
          </ReactEDSWrapper>
        </div>
      )
    })
  ];

  const table = useReactTable({
    data: data,
    columns: columns(),
    onPaginationChange: setPagination,
    manualPagination: true,
    rowCount: data.length,
    getCoreRowModel: getCoreRowModel(),
    /*debugTable: true,
            debugHeaders: true,
            debugColumns: false,*/
    state: {
      //...
      pagination
    },
    manualFiltering: true,
    onColumnFiltersChange: (updater) => {
      const newFilters = typeof updater === 'function' ? updater(columnFilters) : updater;
      if (filterTimeout) clearTimeout(filterTimeout);
      const timeout = setTimeout(() => {
        setColumnFilters(newFilters);
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      }, 500);
      setFilterTimeout(timeout);
    }
  });

  const getItemNumbers = () => {
    if (completeData) {
      return `${completeData?.total > completeData.limit ? completeData.data.length + completeData.offset : completeData.total} of ${completeData.total} items`;
    }
  };

  return (
    <>
      <div>
        <span style={{ color: '#878787' }}>{getItemNumbers()}</span>
        <div className={`table ${styles.tableContainer}`}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse'
            }}
          >
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{
                        textAlign: 'left',
                        padding: '12px 16px'
                      }}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.id !== 'actions' &&
                        <input
                          type="text"
                          value={header.column.getFilterValue() as string}
                          onChange={(e) => header.column.setFilterValue(e.target.value)}
                          placeholder={`Filter`}
                          className={styles.filterInput}
                        />
                      }
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            {completeData?.data.length === 0 && (
              <tbody>
                <tr>
                  <td colSpan={5}>
                    <div className={styles.noDataContainer}>
                      <i className={`icon icon-info ${styles.dangerIcon}`} />
                      <div className={styles.noDataTitle}>No Test Collections Found</div>
                    </div>
                  </td>
                </tr>
              </tbody>
            )}
            {completeData && completeData?.data?.length > 0 && (
              <tbody>
                {table.getPaginationRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    style={{
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = !theme ? '#404040' : '#DCDCDC';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '';
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        style={{
                          padding: '12px 16px',
                          borderTop: `1px solid ${!theme ? '#404040' : '#242424'}`
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        <ReactEDSWrapper
          component={Pagination}
          numPages={table.getPageCount()}
          selectedPage={table.getState().pagination.pageIndex + 1}
          onPaginationChangePage={(page: number) => {
            table.setPageIndex(page - 1);
            fetchTestCollections(page - 1);
          }}
          optionsList={[
            { value: '10', label: '10', active: table.getState().pagination.pageSize === 10 },
            { value: '20', label: '20', active: table.getState().pagination.pageSize === 20 },
            { value: '30', label: '30', active: table.getState().pagination.pageSize === 30 },
            { value: '40', label: '40', active: table.getState().pagination.pageSize === 40 },
            { value: '50', label: '50', active: table.getState().pagination.pageSize === 50 }
          ]}
          onPaginationChangeSelect={(size: string) => {
            table.setPageSize(Number(size));
            table.setPageIndex(0);
            fetchTestCollections(0, Number(size));
          }}
          withActions
        />
      </div>
      {selectedTC && (
        <DeleteTestCollectionDialog
          open={dialogOpen}
          testCollection={selectedTC}
          onDelete={() => {
            setSelectedTC(undefined);
            setDialogOpen(false);
            const { pageIndex, pageSize } = pagination;
            fetchTestCollections(pageIndex, pageSize);
          }}
          onCancel={() => {
            setSelectedTC(undefined);
            setDialogOpen(false);
          }}
        />
      )}
    </>
  );
}
export default TestCollectionList;
